export type PlanFormExerciseDraft = {
  rowKey: string;
  id?: string;
  name: string;
  sets: string;
  reps: string;
  restSeconds: string;
  notes: string;
  movementId?: string | null;
  videoId?: string | null;
  videoSource: 'library' | 'custom' | 'none';
  uploadState: 'idle' | 'uploading' | 'error';
  uploadError?: string;
  uploadToken?: string;
  notice?: string;
};

export type PlanFormDraft = {
  clientId: string;
  name: string;
  exercises: PlanFormExerciseDraft[];
};

export type PlanFormNavigationType = 'POP' | 'PUSH' | 'REPLACE';

const RESUME_KEY = 'coachapp:plan-form-draft-resume';

export function planFormDraftStorageKey(clientId: string, planId?: string): string {
  return `coachapp:plan-form-draft:${clientId}:${planId ?? 'new'}`;
}

export function savePlanFormDraftForVideoLimit(key: string, draft: PlanFormDraft): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(draft));
    sessionStorage.setItem(RESUME_KEY, key);
  } catch {
    /* quota / private mode */
  }
}

function readStorage(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function clearPlanFormDraft(key: string): void {
  try {
    sessionStorage.removeItem(key);
    if (sessionStorage.getItem(RESUME_KEY) === key) {
      sessionStorage.removeItem(RESUME_KEY);
    }
  } catch {
    /* ignore */
  }
}

function isPlanFormDraft(value: unknown, clientId: string): value is PlanFormDraft {
  if (!value || typeof value !== 'object') return false;
  const draft = value as PlanFormDraft;
  return draft.clientId === clientId && typeof draft.name === 'string' && Array.isArray(draft.exercises);
}

export function claimAbandonedPlanFormDraft(input: {
  key: string;
  clientId: string;
  navigationType: PlanFormNavigationType;
  locationState: { planFormDraftKey?: string } | null;
}): PlanFormDraft | null {
  const resume = readStorage(RESUME_KEY);
  const signaled = input.locationState?.planFormDraftKey === input.key;
  const popped = input.navigationType === 'POP' && resume === input.key;
  if (!signaled && !popped) {
    if (resume === input.key && input.navigationType !== 'POP') {
      clearPlanFormDraft(input.key);
    }
    return null;
  }

  const raw = readStorage(input.key);
  clearPlanFormDraft(input.key);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isPlanFormDraft(parsed, input.clientId) ? parsed : null;
  } catch {
    return null;
  }
}
